import React, { useState } from 'react';
import { ChatCircle, X } from '@phosphor-icons/react';
import { BuilderResponseStrip } from './BuilderResponseControls';

/**
 * Phase 1.5 — Homeowner-side wrapper around BuilderResponseStrip:
 *   - Always renders the strip (cyan = approved, amber = rejected,
 *     blue = disputed).
 *   - Adds a "Dispute" button on rejected snags so the homeowner can
 *     formally challenge the rejection. Disputes carry a free-text
 *     reason that travels with the snag forever.
 *   - Hides itself entirely when there's no builder response or
 *     dispute to display.
 *
 * The dispute mutation goes through the standard PATCH /snags/{id}
 * endpoint via the `onDispute({ disputed, dispute_reason })` callback.
 * That keeps the offline queue in useProjectsAPI working transparently.
 */
export default function BuilderResponseHomeownerView({ snag, onDispute }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState(snag.dispute_reason || '');
    const [busy, setBusy] = useState(false);

    const resp = (snag.builder_response || '').toLowerCase();
    const disputed = !!snag.disputed;
    if (!resp && !disputed) return null;

    const submitDispute = async () => {
        if (!reason.trim()) return;
        setBusy(true);
        try {
            await onDispute?.({ disputed: true, dispute_reason: reason.trim() });
            setOpen(false);
        } finally {
            setBusy(false);
        }
    };

    const withdraw = async () => {
        setBusy(true);
        try {
            await onDispute?.({ disputed: false });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-2" data-testid={`builder-view-${snag.id}`}>
            <BuilderResponseStrip snag={snag} />

            {/* Dispute action — only when builder rejected and homeowner
                hasn't already disputed. */}
            {resp === 'rejected' && !disputed && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid={`dispute-snag-${snag.id}`}
                    className="mt-2 inline-flex items-center gap-1 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider border border-[#2563EB] text-[#1E3A8A] hover:bg-[#DBEAFE] active:scale-95 transition-all duration-150"
                >
                    <ChatCircle size={12} weight="bold" /> Dispute rejection
                </button>
            )}

            {/* Withdraw an existing dispute — useful if the homeowner
                changes their mind after a discussion with the builder. */}
            {disputed && (
                <button
                    onClick={withdraw}
                    disabled={busy}
                    data-testid={`withdraw-dispute-${snag.id}`}
                    className="mt-2 inline-flex items-center gap-1 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[#52525B] hover:bg-[#F4F4F5] active:scale-95 transition-all duration-150"
                >
                    Withdraw dispute
                </button>
            )}

            {open && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4 no-print" data-testid="dispute-modal">
                    <div className="bg-white w-full max-w-md border border-[#18181B]">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-[#18181B]">
                            <div className="flex items-center gap-2">
                                <ChatCircle size={18} weight="bold" color="#2563EB" />
                                <span className="font-display font-semibold">Dispute rejection</span>
                            </div>
                            <button onClick={() => setOpen(false)} aria-label="Close" className="p-1 hover:bg-[#F4F4F5]"><X size={18} /></button>
                        </div>
                        <div className="p-4">
                            <div className="text-sm text-[#52525B]">
                                The builder rejected <strong className="text-[#09090B]">{snag.title}</strong>. Tell them why you disagree — this gets stamped to your report and kept as part of the audit trail.
                            </div>
                            <textarea
                                rows={4}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Why this should still be classed as a defect (e.g. NHBC chapter, photos taken on completion day, builder's prior agreement…)"
                                data-testid="dispute-reason-input"
                                className="w-full mt-4 border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2 text-sm"
                                maxLength={1000}
                            />
                            <div className="text-[10px] text-[#A1A1AA] font-mono uppercase tracking-wider mt-1 text-right">
                                {reason.length}/1000
                            </div>
                            <div className="flex gap-2 mt-5">
                                <button
                                    disabled={busy || !reason.trim()}
                                    onClick={submitDispute}
                                    data-testid="dispute-submit-btn"
                                    className="flex-1 bg-[#2563EB] text-white py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1D4ED8] disabled:opacity-50 active:scale-95 transition-all duration-150"
                                >{busy ? 'Saving…' : 'Send dispute'}</button>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-[#18181B] hover:bg-[#F4F4F5] active:scale-95 transition-all duration-150"
                                >Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
